import React, { useCallback, useEffect, useMemo } from 'react';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import { Box } from '@televet/kibble-ui/build/chakra';

interface IPromptPreviewProps {
  prompt: string;
  questionIndex: number;
  inputValues: string[][];
  setInputValues: React.Dispatch<React.SetStateAction<string[][]>>;
}

const PromptPreview = ({ prompt, questionIndex, inputValues, setInputValues }: IPromptPreviewProps): JSX.Element => {
  const formatSegment = useCallback(
    (segment: string, index: number, blankIndex: number): JSX.Element | string => {
      const value =
        inputValues[questionIndex] && inputValues[questionIndex][index]
          ? inputValues[questionIndex][index]
          : `(${blankIndex})`;
      return segment === WildcardValues.FillInTheBlank ? (
        <Box
          key={`previewSegment-${index}`}
          as="span"
          px={5}
          bg="white"
          border="1px"
          borderColor="border.default"
          fontWeight={600}
          fontSize="xs"
          borderRadius="md"
        >
          {value}
        </Box>
      ) : (
        segment
      );
    },
    [inputValues, questionIndex],
  );

  const segments = useMemo(() => {
    const fillInTheBlankRegex = new RegExp(`(${WildcardValues.FillInTheBlank})`, 'g');
    return prompt.split(fillInTheBlankRegex).filter(Boolean);
  }, [prompt]);

  useEffect(() => {
    for (let i = 0; i < segments.length; i++) {
      setInputValues((currentValues) => {
        const newValues = [...currentValues];
        if (!newValues[questionIndex]) {
          newValues[questionIndex] = [];
        }

        // include empty entries for non-FITBs for easier indexing
        if (!newValues[questionIndex][i] && segments[i] === WildcardValues.FillInTheBlank) {
          newValues[questionIndex][i] = '';
        }

        return newValues;
      });
    }
  }, [questionIndex, segments, setInputValues]);

  return (
    <Box display="inline-block">
      {segments.map((string, index) => {
        const blankIndex =
          segments.slice(0, index).filter((segment) => segment === WildcardValues.FillInTheBlank).length + 1;
        return formatSegment(string, index, blankIndex);
      })}
    </Box>
  );
};

export default PromptPreview;
